import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TeamMember = ({ name, title, image, bio }) => {
  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10 hover:border-purple-500/50">
      <div className="h-64 bg-gray-800 relative overflow-hidden">
        <img 
          src={image} 
          alt={name} 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
        <p className="text-purple-400 font-medium mb-4">{title}</p>
        <p className="text-gray-300">{bio}</p>
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h1 className="text-4xl font-bold mb-6">
                About ZemedicAI
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                  Transforming Diagnostics Across Africa
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                ZemedicAI is building solar-powered AI diagnostic booths for instant medical imaging and disease detection — even without internet or electricity.
              </p>
              <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 p-6 rounded-xl border border-purple-500/20">
                <p className="text-gray-300 italic">
                  “After nearly losing my life to a misdiagnosed injury as a child, I made it my mission to ensure no African suffers or dies due to lack of timely diagnosis.”
                </p>
                <p className="text-white font-medium mt-4">— Emmanuel Ezeji, Founder & CEO ZemedicAI</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/20 to-indigo-900/20 rounded-xl overflow-hidden border border-gray-800">
              <img 
                src="/images/zemedicai-booth.png" 
                alt="ZemedicAI Team" 
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Leadership Team */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Our Leadership Team</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <TeamMember 
                name="Emmanuel Ezeji"
                title="Founder & CEO ZemedicAI"
                image="/images/ceo.jpg"
                bio="AI engineer and Computer Scientist with a background in medical AI, computer vision, and global research contributions. Leads product innovation at ZemedicAI."
              />
              <TeamMember 
                name="Dr. Obidi Valentine"
                title="Clinical Lead"
                image="/images/obidi.png"
                bio="Chief Medical Officer at the Federal Medical Center, Anambra, Nigeria; Doctor-owner of St. Dominic Savio Hospital, Nigeria. Advises on clinical workflows and patient interactions for ZemedicAI booths."
              />
              <TeamMember 
                name="Eng. Nitesh Rijal"
                title="CTO"
                image="/images/cto.png"
                bio="Senior AI/DevOps engineer and Founder of NeuralPass. Oversees & leads the technical team for AI Training, Deployment and SDK/API integrations across partner telehealth platforms."
              />
              <TeamMember
                name="Oseni Oladapo"
                title="COO"
                image="/images/coo.jpg"
                bio="Head of Media Operations to the Wife of the Governor, Osun State (prev); Founder/CEO TMG Studios"
              />
            </div>
          </div>

          {/* Our Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="bg-gradient-to-br from-purple-900/20 to-indigo-900/20 p-8 rounded-xl border border-purple-500/30">
              <h3 className="text-2xl font-bold mb-6">Our Mission</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To democratize expert-level diagnosis by using AI, solar power, and offline tools — making world-class diagnostic services accessible even in the most remote corners of Africa.
              </p>
            </div>
            <div className="bg-gradient-to-br from-indigo-900/20 to-purple-900/20 p-8 rounded-xl border border-indigo-500/30">
              <h3 className="text-2xl font-bold mb-6">Our Vision</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                A continent where no life is lost due to lack of early diagnosis — where every person, regardless of location or income, gets fast, affordable, and accurate diagnostic care.
              </p>
            </div>
          </div>

          {/* Our Impact */}
          <div className="bg-gray-900 rounded-xl p-10 border border-gray-800 mb-20">
            <h2 className="text-3xl font-bold mb-10 text-center">Our Impact</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 mb-2">5</div>
                <p className="text-gray-300 font-medium">Partner Institutions</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 mb-2">6</div>
                <p className="text-gray-300 font-medium">Diagnostic Modalities</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 mb-2">80%</div>
                <p className="text-gray-300 font-medium">Clinician Agreement (200-Case Sample)</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 mb-2">2</div>
                <p className="text-gray-300 font-medium">Countries Deployed (Nigeria, Kenya)</p>
              </div>
            </div>
          </div>

          {/* Recognition & Programs */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-10 text-center">Recognition & Programs</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <h3 className="text-xl font-semibold mb-3">Slush'D Lagos–Finland 2025</h3>
                <p className="text-gray-300">
                  Finalist at Slush'D Lagos–Finland 2025.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <h3 className="text-xl font-semibold mb-3">MIT Deep Tech (Kuo Sharper Center)</h3>
                <p className="text-gray-300">
                  Selected for the MIT Deep Tech program at the Kuo Sharper Center — co-organized by the President of Botswana and the Massachusetts Institute of Technology.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <h3 className="text-xl font-semibold mb-3">JaliMindCare, Kenya</h3>
                <p className="text-gray-300">
                  Clinical and telehealth partnership with JaliMindCare, one of Kenya's leading telehealth platforms.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-2xl p-10 text-center border border-purple-500/30">
            <h2 className="text-3xl font-bold mb-4">Partner With Us</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              NGOs, governments, health facilities and global health partners — let’s work together to deploy diagnostic booths that will change millions of lives.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/partners" 
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium"
              >
                Become a Partner
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-3 bg-black border border-purple-500 text-purple-400 hover:bg-purple-900/20 rounded-lg font-medium"
              >
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
